import { GENERATE_COMMENTARY_API, GenerateCommentaryResult } from '@aloft/toolkit-shared';
import { UseMutationOptions, useMutation } from '@tanstack/react-query';

import { z } from 'zod';

import { ToolkitFetcherConfig, useToolkitContext } from '../toolkitContext.ts';
import { toolkitFetch } from '../toolkitFetcher.ts';

const API_SCHEMA = GENERATE_COMMENTARY_API.POST;
type GenerateCommentaryBody = z.infer<typeof API_SCHEMA.REQUEST.BODY>;

export const useGenerateCommentary = (
  options?: Omit<
    UseMutationOptions<GenerateCommentaryResult, Error, GenerateCommentaryBody>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();

  return useMutation<GenerateCommentaryResult, Error, GenerateCommentaryBody>({
    mutationFn: async (variables) => {
      const body = API_SCHEMA.REQUEST.BODY.parse(variables);
      const response = await toolkitFetch<
        unknown,
        { status: number | 'unknown'; payload: unknown },
        GenerateCommentaryBody,
        NonNullable<ToolkitFetcherConfig['headers']>,
        NonNullable<ToolkitFetcherConfig['queryParams']>,
        Record<string, string>
      >({
        url: '/api/v2/aiPrompts/generateCommentary',
        method: 'post',
        body,
        ...fetcherOptions,
      });
      if (response && typeof response === 'object' && 'error' in response) {
        const message = (response as { error?: unknown }).error;
        throw new Error(typeof message === 'string' ? message : 'Unable to generate commentary');
      }
      return API_SCHEMA.RESPONSE.parse(response);
    },
    ...options,
  });
};
